import { motion } from 'framer-motion'
import Link from './Link'
import type { Product } from '../data/products'
import { getCategorySeoContent } from '../data/seo-content'

type CategoryPageProps = {
  category: string
  items: Product[]
}

const introByCategory: Record<string, string> = {
  'Knee Joint':
    'Prosthetic knee joints control stability during stance and swing. We supply hydraulic and lightweight mechanical knees matched to different activity levels, from early rehabilitation training to confident daily walking.',
  Foot:
    'Carbon fiber prosthetic feet store and return energy with every step. Our range covers dynamic response feet for active users as well as dependable everyday solutions for below-knee and above-knee fittings.',
  'Myoelectric Hand':
    'Myoelectric hands are driven by EMG signals picked up from the residual muscles, giving users intuitive grip control without harnesses or cables.',
  'Silicone Prosthesis':
    'Silicone prostheses restore natural appearance for partial hand and partial foot amputation. Medical-grade silicone, custom skin tones and hand-finished detail make each piece discreet in daily life.',
  Liner:
    'The liner is the interface between the residual limb and the socket. Cushioning, suspension and friction control all start here, which is why liner choice decides day-long wearing comfort.',
  Adapter:
    'Adapters connect sockets, pylons, knees and feet in a modular prosthesis, and carry the alignment adjustments that determine gait quality and comfort.',
  Material:
    'Workshop materials for socket fabrication and repair, including carbon fiber cloth used for laminated custom sockets.',
  Socks:
    'Stump socks and cosmetic socks manage volume changes, reduce abrasion and extend the service life of silicone liners.',
  'Shuttle Lock':
    'Shuttle locks anchor the liner inside the socket so the residual limb stays secure through the full walking cycle.'
}

const CategoryPage = ({ category, items }: CategoryPageProps) => {
  const slug = items[0]?.categorySlug ?? ''
  const seoContent = getCategorySeoContent(slug)
  const intro =
    introByCategory[category] ??
    `${category} products supplied by Angel Technology International Co., Ltd. for global rehabilitation and prosthetic buyers.`

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: category,
    itemListElement: items.map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://www.angeltechpo.com.cn/product/${product.slug}`,
      name: product.name
    }))
  }

  const faqJsonLd = seoContent
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: seoContent.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer }
        }))
      }
    : undefined

  return (
    <main className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
      />
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}

      <section className="bg-slate-950 pb-12 pt-32 text-white">
        <div className="container mx-auto px-4">
          <nav aria-label="Breadcrumb" className="text-sm text-slate-400">
            <Link to="/" className="transition-colors hover:text-white">
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="text-slate-200">{category}</span>
          </nav>
          <p className="mt-6 text-sm font-semibold uppercase tracking-[0.24em] text-teal-300">
            {items.length} {items.length === 1 ? 'product' : 'products'}
          </p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight md:text-5xl">{category}</h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">{intro}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {items.map((product, index) => (
              <motion.article
                key={product.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.24) }}
                className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-xl hover:shadow-slate-200/80"
              >
                <Link to={`/product/${product.slug}`} className="block bg-gradient-to-b from-white to-slate-50 p-6">
                  <img
                    src={product.image}
                    alt={product.alt}
                    width={310}
                    height={310}
                    className="mx-auto aspect-square w-full max-w-[310px] object-contain transition-transform duration-300 group-hover:scale-[1.03]"
                  />
                </Link>
                <div className="border-t border-slate-100 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Link
                      to={`/category/${slug}`}
                      className="rounded-md bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700"
                    >
                      {product.category}
                    </Link>
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                      {product.code}
                    </span>
                  </div>
                  <h2 className="mt-4 text-2xl font-semibold leading-tight text-slate-950">
                    <Link to={`/product/${product.slug}`} className="transition-colors hover:text-teal-700">
                      {product.name}
                    </Link>
                  </h2>
                  <p className="mt-3 text-sm leading-7 text-slate-600">{product.summary}</p>
                  <ul className="mt-5 space-y-2 border-t border-slate-100 pt-5">
                    {product.details.map((detail) => (
                      <li key={detail} className="flex gap-3 text-sm leading-6 text-slate-600">
                        <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-teal-600" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/product/${product.slug}`}
                    className="mt-5 inline-block text-sm font-semibold text-teal-700 hover:text-teal-800"
                  >
                    View specifications →
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>

          {seoContent && (
            <section className="mt-20 border-t border-slate-200 pt-14">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-teal-700">
                Procurement resource
              </p>
              <h2 className="mt-3 text-3xl font-semibold text-slate-950">Buyer guide</h2>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                {seoContent.guide.map((section) => (
                  <article key={section.title} className="rounded-lg bg-slate-50 p-7 ring-1 ring-slate-200">
                    <h3 className="text-xl font-semibold text-slate-950">{section.title}</h3>
                    <p className="mt-3 text-base leading-8 text-slate-600">{section.body}</p>
                  </article>
                ))}
              </div>

              <h2 className="mt-14 text-3xl font-semibold text-slate-950">Common buyer questions</h2>
              <div className="mt-6 grid gap-5 md:grid-cols-2">
                {seoContent.faq.map((item) => (
                  <article key={item.question} className="rounded-lg border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-slate-950">{item.question}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-600">{item.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          )}

          <p className="mt-12 text-sm text-slate-500">
            <Link to="/#portfolio" className="font-semibold text-teal-700 hover:text-teal-800">
              ← Back to all product categories
            </Link>
          </p>
        </div>
      </section>
    </main>
  )
}

export default CategoryPage
